import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import Card from "@midland-industries/Components/Card";
import Group from "@midland-industries/Components/Group";
import Stack from "@midland-industries/Components/Stack";
import { colors } from "@midland-industries/Styles/colors";
import React from "react";

const enum fields {
    cards = "cards",
    cardTitle = "cardTitle",
    heading = "heading",
    subheading = "subheading",
    content = "content",
    shopNowLink = "shopNowLink",
    contentImage = "contentImage",
}

interface CardModel {
    fields: {
        [fields.cardTitle]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.content]: string;
        [fields.shopNowLink]: LinkFieldValue;
        [fields.contentImage]: string;
    };
}

interface NewProductAnnouncementsProps extends WidgetProps {
    fields: {
        [fields.cards]: CardModel[];
    };
}

const NewProductAnnouncements: React.FC<NewProductAnnouncementsProps> = ({ fields: { cards } }) => {
    return cards ? (
        <Stack gap={15}>
            <Group justify="space-between" align="center" style={{ marginBottom: 9 }}>
                <Typography size={24} color={colors.textAccent} transform="uppercase">
                    New Product Announcements
                </Typography>
                <Link href="/">View all new products</Link>
            </Group>
            <Card borderRadius={10}>
                <Group style={{ height: 389, padding: "30px 30px 30px 60px" }} gap={18} align="center">
                    <Stack style={{ width: 422 }} gap={9}>
                        <Typography lineHeight="24px" color={colors.textAccent} transform="uppercase">
                            {cards[0].fields.heading}
                        </Typography>
                        <Typography lineHeight="38px" size={32} weight={700} color={colors.textMain}>
                            {cards[0].fields.subheading}
                        </Typography>
                        <Typography lineHeight="24px" color={colors.textAccent}>
                            {cards[0].fields.content}
                        </Typography>
                        <Link href={cards[0].fields.shopNowLink.value}>Shop now</Link>
                    </Stack>
                    <Stack justify="center" align="center" style={{ overflow: "hidden" }}>
                        <Img
                            src={cards[0].fields.contentImage}
                            style={{ minWidth: "100%", minHeight: "100%", flexShrink: 0 }}
                        />
                    </Stack>
                </Group>
            </Card>
            <Group gap={15} align="center">
                <Card
                    width={547}
                    height={575}
                    borderRadius={10}
                    cardStyles={{ padding: "47px 60px 30px 60px" }}
                    gap={12}
                >
                    <Stack style={{ width: 427 }} gap={9}>
                        <Typography lineHeight="24px" color={colors.textAccent} transform="uppercase">
                            {cards[1].fields.heading}
                        </Typography>
                        <Typography lineHeight="38px" size={32} weight={700} color={colors.textMain}>
                            {cards[1].fields.subheading}
                        </Typography>
                        <Typography lineHeight="24px" color={colors.textAccent}>
                            {cards[1].fields.content}
                        </Typography>
                        <Link href={cards[1].fields.shopNowLink.value}>Shop now</Link>
                    </Stack>
                    <Stack justify="center" align="center" style={{ overflow: "hidden" }}>
                        <Img
                            src={cards[1].fields.contentImage}
                            style={{ minWidth: "100%", minHeight: "100%", flexShrink: 0 }}
                        />
                    </Stack>
                </Card>
                <Card
                    width={547}
                    height={575}
                    borderRadius={10}
                    cardStyles={{ padding: "47px 60px 30px 60px" }}
                    gap={12}
                >
                    <Stack style={{ width: 427 }} gap={9}>
                        <Typography lineHeight="24px" color={colors.textAccent} transform="uppercase">
                            {cards[2].fields.heading}
                        </Typography>
                        <Typography lineHeight="38px" size={32} weight={700} color={colors.textMain}>
                            {cards[2].fields.subheading}
                        </Typography>
                        <Typography lineHeight="24px" color={colors.textAccent}>
                            {cards[2].fields.content}
                        </Typography>
                        <Link href={cards[2].fields.shopNowLink.value}>Shop now</Link>
                    </Stack>
                    <Stack justify="center" align="center" style={{ overflow: "hidden" }}>
                        <Img
                            src={cards[2].fields.contentImage}
                            style={{ minWidth: "100%", minHeight: "100%", flexShrink: 0 }}
                        />
                    </Stack>
                </Card>
            </Group>
        </Stack>
    ) : (
        <></>
    );
};

const newProductAnnouncements: WidgetModule = {
    component: NewProductAnnouncements,
    definition: {
        displayName: "New Product Announcements",
        icon: "presentation-screen",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { cardTitle } }) => cardTitle || "Card",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: fields.cardTitle,
                        editorTemplate: "TextField",
                        defaultValue: "Card",
                    },
                    {
                        name: fields.heading,
                        editorTemplate: "TextField",
                        displayName: "Heading",
                        defaultValue: "",
                    },
                    {
                        name: fields.subheading,
                        editorTemplate: "TextField",
                        displayName: "Subheading",
                        defaultValue: "",
                    },
                    {
                        name: fields.content,
                        displayName: "Content",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.shopNowLink,
                        displayName: "Shop Now Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: fields.contentImage,
                        displayName: "Content Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default newProductAnnouncements;
